'use client';

/**
 * "Here are the new keys" panel on a level page.
 *
 * Tapping a key previews the hand position for it. That turns a static SEO
 * page into something a child can actually explore before committing to a
 * lesson, and it lets a parent see the teaching model in five seconds.
 */
import React, { useState } from 'react';
import type { LevelDef } from '../types';
import KidsKeyboard, { FingerLegend } from './Keyboard';
import HandsGuide from './HandsGuide';
import { fingerHint } from '../lib/keyboard';

export default function LevelKeyPreview({ level }: { level: LevelDef }) {
  const [selected, setSelected] = useState<string>(level.newKeys[0] ?? '');

  return (
    <section className="k-card-lg p-5">
      <h2 className="text-base mb-1">New keys in this level</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Tap a key to see which finger presses it.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {level.newKeys.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setSelected(key)}
            aria-pressed={selected === key}
            className="k-btn k-btn-secondary"
            style={{
              minWidth: 56,
              fontFamily: 'var(--font-mono)',
              ...(selected === key
                ? { borderColor: `var(${level.color})`, color: `var(${level.color})` }
                : {}),
            }}
          >
            {key === ' ' ? 'space' : key.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-[200px_1fr] items-center">
        <HandsGuide char={selected} compact className="mx-auto max-w-[220px] w-full" />
        <div>
          <KidsKeyboard
            nextChar={selected}
            availableKeys={level.cumulativeKeys}
            size="sm"
            showFingerColors
          />
          <p className="text-center text-sm font-bold mt-3">{selected && fingerHint(selected)}</p>
        </div>
      </div>

      <FingerLegend className="mt-5 pt-4" />
    </section>
  );
}
